import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BASEMAP_ADDON_GIS,
  BASEMAP_ESRI_NAVIGATION_DARK,
  BASEMAP_ESRI_NAVIGATION_LIGHT,
  BASEMAP_ESRI_SATELLITE,
  BASEMAP_GOOGLE_HYBRID,
  BASEMAP_GOOGLE_ROADMAP,
} from '@rsos/constants/basemapAddonNames';
import { ESRI, GOOGLE } from '@rsos/constants/mapProviders';
import useSelectedTheme from '@rsos/hooks/useSelectedTheme';
import { setBasemap, setMapAddon, setMapProvider } from '@rsos/sinatra';
import { DARK_THEME, LIGHT_THEME } from '@rsos/theme/constants';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import usePortalType from '@rsos/utils/usePortalType';

/**
 * Determine which initial map provider to use on load and store the values in the sinatra.ui state.
 * @returns String
 */
const useMapProvider = () => {
  const dispatch = useDispatch();

  const themeName = useSelectedTheme();

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const isSatelliteBasemap = useMemo(() => {
    return (
      basemap === BASEMAP_ESRI_SATELLITE || basemap === BASEMAP_GOOGLE_HYBRID
    );
  }, [basemap]);

  const { isPremiumUser } = usePortalType();

  const hasEsriMapAccess = useSelector(state =>
    checkEsriMapAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const [initialMapProvider, setInitialMapProvider] = useState('');

  const setDefaultMap = useCallback(
    mapProvider => {
      if (mapProvider === ESRI) {
        setInitialMapProvider(ESRI);

        dispatch(setMapProvider(ESRI));

        dispatch(
          setBasemap(
            themeName === LIGHT_THEME
              ? BASEMAP_ESRI_NAVIGATION_LIGHT
              : BASEMAP_ESRI_NAVIGATION_DARK,
          ),
        );

        // Append 'gis' to the 'mapAddons' array since the /gis-services endpoint is fetched after
        // the map provider is set to ESRI. The GIS addon in the basemap menu is only rendered if
        // the authority has GIS addon (cached-basemap service).
        dispatch(setMapAddon(BASEMAP_ADDON_GIS));
      } else if (mapProvider === GOOGLE) {
        setInitialMapProvider(GOOGLE);

        dispatch(setMapProvider(GOOGLE));

        dispatch(setBasemap(BASEMAP_GOOGLE_ROADMAP));
      }
    },
    [dispatch, themeName],
  );

  // Set the map provider and map addon for the first time.
  useEffect(() => {
    // If there is already a mapProvider, then user is already logged in and can skip the check.
    if (mapProvider) {
      setInitialMapProvider(mapProvider);

      // If the user is using Esri map but the authority's capability changed to non-premium, then
      // set the mapProvider to google.
      if (mapProvider === ESRI && !(isPremiumUser && hasEsriMapAccess)) {
        setDefaultMap(GOOGLE);
      }
    } else if (isPremiumUser && hasEsriMapAccess) {
      setDefaultMap(ESRI);
    } else {
      setDefaultMap(GOOGLE);
    }
  }, [hasEsriMapAccess, isPremiumUser, mapProvider, setDefaultMap]);

  // On page load, ensure Esri basemap matches the theme.
  useEffect(() => {
    if (!initialMapProvider && mapProvider === ESRI && !isSatelliteBasemap) {
      if (
        themeName === LIGHT_THEME &&
        basemap !== BASEMAP_ESRI_NAVIGATION_LIGHT
      ) {
        dispatch(setBasemap(BASEMAP_ESRI_NAVIGATION_LIGHT));
      } else if (
        themeName === DARK_THEME &&
        basemap !== BASEMAP_ESRI_NAVIGATION_DARK
      ) {
        dispatch(setBasemap(BASEMAP_ESRI_NAVIGATION_DARK));
      }
    }
  }, [
    basemap,
    dispatch,
    initialMapProvider,
    isSatelliteBasemap,
    mapProvider,
    themeName,
  ]);

  return initialMapProvider;
};

export default useMapProvider;
